import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { CalendarCheck, Mail, Phone, Building2, Clock, RotateCcw, CheckCircle2, XCircle, CalendarX } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new",        label: "New",        color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  { value: "scheduled",  label: "Scheduled",  color: "bg-blue-100 text-blue-800 border-blue-300",     icon: CalendarCheck },
  { value: "completed",  label: "Completed",  color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  { value: "no_show",    label: "No show",    color: "bg-rose-100 text-rose-800 border-rose-300",      icon: CalendarX },
  { value: "cancelled",  label: "Cancelled",  color: "bg-slate-100 text-slate-600 border-slate-300",   icon: XCircle },
];
const STATUS_BY_VALUE = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const fmtDateTime = (iso) => new Date(iso).toLocaleString(undefined, {
  weekday: "short", month: "short", day: "numeric",
  hour: "2-digit", minute: "2-digit",
});

export default function AdminDemoRequests() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null); // id currently updating

  const load = () => {
    api.get(`/admin/demo-requests?status=${filter}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        console.warn("demo-requests load failed", e);
        setData(false);
      });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const updateStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.put(`/admin/demo-requests/${id}/status`, { status });
      toast.success(`Marked ${STATUS_BY_VALUE[status]?.label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Status update failed");
    } finally {
      setBusy(null);
    }
  };

  if (data === null) return <DashboardShell><div className="p-8 text-slate-500">Loading demo requests…</div></DashboardShell>;
  if (data === false) return <DashboardShell><div className="p-8 text-red-600">Admin access required.</div></DashboardShell>;

  const counts = data.counts || {};
  const filterPills = [
    { value: "all", label: "All", count: data.total },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label, count: counts[s.value] || 0 })),
  ];

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-8">
          <div className="overline text-slate-500">Growth · Pipeline</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Demo Requests</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every "Book a demo" from the public landing page lands here. Move each one through the funnel
            as you schedule, complete, or reconcile no-shows.
          </p>
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="demo-status-filter">
          {filterPills.map((p) => (
            <button
              key={p.value}
              onClick={() => setFilter(p.value)}
              data-testid={`demo-filter-${p.value}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${
                filter === p.value
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {p.label}
              <span className={`font-mono ${filter === p.value ? "text-white/70" : "text-slate-400"}`}>{p.count}</span>
            </button>
          ))}
        </div>

        {data.items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400" data-testid="demo-requests-empty">
            <CalendarCheck className="w-7 h-7 text-slate-300 mx-auto mb-3" />
            <div className="text-sm font-semibold text-slate-700">No demo requests {filter !== "all" ? `with status "${filter}"` : "yet"}</div>
            <div className="text-xs mt-1">They'll appear here as prospects book from the landing page.</div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="demo-requests-list">
            <ul className="divide-y divide-slate-100">
              {data.items.map((r) => (
                <DemoRow key={r.id} req={r} onUpdate={updateStatus} busy={busy === r.id} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function DemoRow({ req, onUpdate, busy }) {
  const status = STATUS_BY_VALUE[req.status] || STATUSES[0];
  const StatusIcon = status.icon;
  const when = fmtDateTime(req.preferred_slot);
  const created = new Date(req.created_at).toLocaleDateString();
  return (
    <li className="p-5" data-testid={`demo-row-${req.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{req.name}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${status.color}`}>
              <StatusIcon className="w-3 h-3" /> {status.label}
            </span>
            {req.utm_source && (
              <span className="text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-600 border border-slate-200 rounded-sm px-2 py-0.5">
                {req.utm_source}
              </span>
            )}
          </div>
          <div className="mt-1.5 text-xs text-slate-500 font-mono flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <a href={`mailto:${req.email}`} className="hover:text-slate-900">{req.email}</a>
            </span>
            {req.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="w-3 h-3" /> {req.phone}
              </span>
            )}
            {req.company && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {req.company}
              </span>
            )}
            {req.portfolio_size && (
              <span className="inline-flex items-center gap-1 text-slate-900 font-bold">
                {req.portfolio_size} properties
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">Requested slot</div>
          <div className="text-sm font-bold text-slate-900 font-mono mt-0.5" data-testid={`demo-slot-${req.id}`}>{when}</div>
          <div className="text-[10px] text-slate-400 font-mono mt-0.5">Submitted {created}</div>
        </div>
      </div>

      {req.message && (
        <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-sm p-3 font-mono whitespace-pre-wrap">
          {req.message}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => onUpdate(req.id, s.value)}
            disabled={busy || req.status === s.value}
            data-testid={`demo-action-${req.id}-${s.value}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-semibold border transition-colors disabled:opacity-50 ${
              req.status === s.value
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            <s.icon className="w-3 h-3" /> {s.label}
          </button>
        ))}
        {req.status !== "new" && (
          <button
            onClick={() => onUpdate(req.id, "new")}
            disabled={busy}
            data-testid={`demo-action-${req.id}-reopen`}
            className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1"
            title="Reopen to new"
          >
            <RotateCcw className="w-3 h-3" /> Reopen
          </button>
        )}
      </div>
    </li>
  );
}
